<template>
  <gc-main-layout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :tabs="tabs"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :selectedIndex="selectedIndex"
    :baseView="baseView"
    :formsConfig="formsConfig"
    :key="pageKey"
    :hot-key-create-path="`/product-unit-of-measure/create/${this.baseView.id}`"
    :hot-key-create-route-id="model.ProductCode"
    :hot-key-grid-tab-index="3"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        title="Product"
        :baseView="baseView"
        :createModeIsEditMode="true"
        :primaryLabel="model.ProductCode"
        :secondaryLabel="model.ProductDescription1"
        recordType="Product"
        v-if="model.ProductCode"
      />
    </template>

    <div class="css-v2 split-view">
      <gc-split-view
        :showLeftContainer="showLeftContainer"
        :showRightContainer="showRightContainer"
      >
        <template #leftContent>
          <div class="css-v2 split-view-list">
            <gc-filter-split-view
              :showLeftContainer="showFilterLeftContainer"
              :showRightContainer="showFilterRightContainer"
            >
              <template #leftContent>
                <dx-data-grid
                  :data-source="dataSourceConfig"
                  ref="productUnitOfMeasureGrid"
                  :columns="columns"
                  :height="gridHeight - 20"
                  @selection-changed="onSelectionChanged"
                  @toolbar-preparing="onToolbarPreparing($event)"
                  @content-ready="addGridNoResults($event)"
                  v-if="model.ProductCode"
                  :selection="{ mode: 'single' }"
                  @exporting="
                    onGridToExcelExporting(
                      $event,
                      `Product - ${baseView.id} - Sales UM`,
                      columns
                    )
                  "
                >
                  <DxPaging :page-size="50" :enabled="false" />
                  <DxPager
                    :showInfo="true"
                    :showPageSizeSelector="false"
                    :infoText="'({2} items)'"
                    :visible="false"
                  />

                  <DxScrolling
                    :mode="'virtual'"
                    :row-rendering-mode="'virtual'"
                    :preloadEnabled="true"
                  />
                  <DxExport :enabled="true" />

                  <template #active-cell-template="{ data }">
                    <GcInactive :inactive="data.data.Inactive" />
                  </template>

                  <template #variable-cell-template="{ data }">
                    <DxCheckBox
                      :value="data.data.IsDefault == 'Y'"
                      :read-only="true"
                    />
                  </template>

                  <template #checkbox-cell-template="{ data }">
                    <DxCheckBox
                      :value="data.data[data.column.dataField] == 'Y'"
                      :read-only="true"
                    />
                  </template>

                  <template #number-cell-template="{ data }">
                    <gc-decimal-cell
                      :data-value="{
                        value: data.data[data.column.dataField],
                        text: formatMyNumber(
                          data.data[data.column.dataField],
                          '#,##0.00'
                        ),
                      }"
                    />
                  </template>

                  <template #branch-cell-template="{ data }">
                    <GcDescriptionCodeCellTemplate
                      :description="data.data.BranchDescription"
                      :code="data.data.BranchCode"
                    />
                  </template>
                </dx-data-grid>
              </template>
              <template #rightContent>
                <gc-advance-filter-panel
                  :toolbar="filterToolbar"
                  :setFilterFormHeight="filterFormHeight"
                  :sideBarTabs="sideBarTabs"
                  :filterFormItems="filterFormItems"
                  :filterModel="getAdvanceFilterModel"
                  :isJson="false"
                  :screenType="'supplier-contact-list'"
                  :data-source-config="dataSourceConfig"
                  :initial-filter="initialFilter"
                  v-if="model.ProductCode"
                >
                </gc-advance-filter-panel>
              </template>
            </gc-filter-split-view>
          </div>
        </template>

        <template #rightContent>
          <DxToolbar class="split-view-toolbar">
            <DxItem #default location="before" locate-in-menu="never">
              <div class="toolbar-label">
                <span class="text-primary">{{ model.BranchDescription }}</span>
                <span class="text-secondary ml-sm">{{ model.BranchCode }}</span>
              </div>
              <!-- <GcHeaderContentDynamic
                  :title="undefined"
                  :baseView="baseView"
                  :primaryLabel="model.BranchDescription"
                  :secondaryLabel="model.BranchCode"
                /> -->
            </DxItem>

            <DxItem
              widget="dxForm"
              location="after"
              :options="{
                formData: model,
                items: toolbarActiveItems,
                validationGroup: 'toolbarForm-2',
                labelLocation: 'left',
                customizeItem: customizeItem,
              }"
              locate-in-menu="auto"
            ></DxItem>
            <DxItem
              :options="toolbar.audit"
              location="after"
              locate-in-menu="auto"
              widget="dxButton"
              v-if="baseView.mode == 'review'"
            />
            <!-- <DxItem
              :options="toolbar.delete"
              location="after"
              locate-in-menu="auto"
              widget="dxButton"
              :visible="formsConfig.editableOption"
              v-if="baseView.mode == 'review'"
            /> -->

            <DxItem
              :options="toolbar.edit"
              location="after"
              locate-in-menu="auto"
              widget="dxButton"
              :visible="formsConfig.editableOption"
              v-if="baseView.mode == 'review'"
            />

            <DxItem
              :options="toolbar.save"
              location="after"
              locate-in-menu="auto"
              widget="dxButton"
              v-if="baseView.mode != 'review'"
            />

            <DxItem
              :options="toolbar.close"
              location="after"
              widget="dxButton"
            />
          </DxToolbar>
          <div class="view-card responsive-paddings">
            <form v-if="loadingCompleted">
              <DxForm
                :form-data.sync="model"
                :read-only="false"
                :show-colon-after-label="false"
                :show-required-mark="false"
                :show-validation-summary="false"
                :validation-group="formsConfig.validationGroup"
                :col-count-by-screen="formsConfig.splitViewColCountByScreen"
                label-location="left"
                :customizeItem="customizeItem"
                :ref="'product-unit-of-measure'"
                :items="items"
              >
                <template #branch="{ data }">
                  <div class="custom-auto-complete">
                    <span class="auto-complete-primary">{{
                      data.Description
                    }}</span>
                    <span class="auto-complete-secondary">{{ data.Code }}</span>
                  </div>
                </template>
                <template #unitOfMeasure="{ data }">
                  <div class="custom-auto-complete">
                    <span class="auto-complete-primary">{{
                      data.Description
                    }}</span>
                    <span class="auto-complete-secondary">{{ data.Code }}</span>
                  </div>
                </template>
              </DxForm>
            </form>
          </div>
        </template>
      </gc-split-view>
    </div>
  </gc-main-layout>
</template>

<script>
import { formatNumber } from "devextreme/localization";
import { BranchService } from "../../_services/api/sales/branch.service";

import BaseViewMixin from "../../v1/_mixins/base.view";
import validationEngine from "devextreme/ui/validation_engine";
import { ProductCostService } from "../../_services/api/products/product-cost.service";
import { ProductService } from "../../_services/api/products/product.service";
import { ProductUnitOfMeasureService } from "../../_services/api/products/product-unit-of-measure.service";
import { UnitOfMeasureService } from "../../_services/api/settings/products/unit-of-measure.service";
import {
  ProductTabs,
  ProductTabsVisibility,
} from "../../_models/tabdata/products/products-tabs";

export default {
  mixins: [BaseViewMixin],
  created() {
    this.selectedIndex = 3;
  },
  mounted() {
    if (this.loadingCompleted)
      validationEngine.validateGroup(this.formsConfig.validationGroup);
  },
  updated() {
    if (this.loadingCompleted)
      validationEngine.validateGroup(this.formsConfig.validationGroup);
  },
  data() {
    return {
      toolbar: {
        close: {
          icon: "close",
          onClick: this.close,
        },
        edit: {
          icon: "edit",

          onClick: () => {
            this.$router.push({
              path: `/product-unit-of-measure/edit/${this.baseView.id}`,
            });
          },
        },
        save: {
          icon: "floppy",
          ref: "saveButton",
          validationGroup: "formsData",
          onClick: (e) => {
            this.$logger.trackEventButtonClickSave();
            this.saveProductUnitOfMeasure(e);
          },
        },
        delete: {
          icon: "trash",
          validationGroup: "formsData",
          onClick: (e) => {
            this.$logger.trackEventButtonClickDelete();
            this.deleteProductUnitOfMeasure(e);
          },
        },
        audit: {
          icon: "clock",

          onClick: () => {
            this.displayAuditPopup(
              this.model.CreatedBy,
              this.model.DateCreated,
              this.model.ChangedBy,
              this.model.DateChanged
            );
          },
        },
      },
      advanceFilterModel: {
        BranchCode: "",
        UnitOfMeasureCode: "",
      },
      model: {
        ProductCode: "",
        ProductDescription1: "",
        BranchCode: "",
        BranchDescription: "",
        SalesUnitOfMeasureCode: "",
        ProductUnitOfMeasureCode: "",
        ConversionFactor: 1.0,
        StandardCostSalesUM: 0.0,
        StandardCostStockUM: 0.0,
        StandardSellSalesUM: 0.0,
        StandardSellStockUM: 0.0,
        GPPercent: 0.0,
        Barcode: "",
        Pieces: 0,
        RecordId: "00000000-0000-0000-0000-000000000000",
        IsDefault: true,
        IsDefaultReadOnly: false,
        AllBranches: true,
        DateChanged: "",
        DateCreated: "",
        ChangedBy: "",
        CreatedBy: "",
        Inactive: false,
        RRP: 0,
        RRPConversionFactor: 0,
        IsVariable: false,
        IsProductionUM: false,
        IsPrimaryProductionUM: false,
      },
      IsProductionUnitVisible: true,
    };
  },
  computed: {
    toolbarActiveItems() {
      return [
        {
          itemType: "group",
          caption: "",
          items: this.parseFormColumns([
            {
              dataField: "AllBranches",
              gcType: "Boolean",
              gcLabel: "Update All Branches",
            },
          ]),
        },
      ];
    },
    initialFilter() {
      return [["ProductCode", "=", this.model.ProductCode]];
    },
    getAdvanceFilterModel() {
      return this.advanceFilterModel;
    },
    filterFormItems() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 1,
          items: this.parseFormColumns([
            {
              dataField: "BranchCode",
              gcLabel: "Branch",
              gcType: "String",
              editorType: "dxTagBox",
              editorOptions: {
                dataSource: ProductService.dataSourceConfig("api/Branch", {
                  select: ["Code", "Description"],
                  sort: [{ selector: "Code", asc: true }],
                  top: 5,
                  filter: ["Inactive", "=", "N"],
                  key: "Code",
                }),
                valueExpr: "Code",
                displayExpr: "Description",
                searchExpr: ["Code", "Description"],
                searchMode: "contains",
                itemTemplate: "item-template",
                searchEnabled: true,
              },
            },
            {
              dataField: "UnitOfMeasureCode",
              gcLabel: "UM",
              gcType: "String",
              editorType: "dxTagBox",
              editorOptions: {
                dataSource: ProductService.dataSourceConfig(
                  "api/UnitOfMeasure",
                  {
                    select: ["Code", "Description"],
                    sort: [{ selector: "Code", asc: true }],
                    top: 5,
                    filter: ["Inactive", "=", false],
                    key: "Code",
                  }
                ),
                valueExpr: "Code",
                displayExpr: "Description",
                searchExpr: ["Code", "Description"],
                searchMode: "contains",
                itemTemplate: "item-template",
                searchEnabled: true,
              },
            },
          ]),
        },
      ];
    },
    columns() {
      return this.parseGridColumns("master", [
        {
          gcLabel: "Branch",
          dataField: "BranchDescription",
          cellTemplate: "branch-cell-template",
          gcType: "String",
          gcSubType: "PrimaryRecord",
          gcExport: ["BranchCode", "BranchDescription"],
        },
        {
          gcLabel: "UM",
          dataField: "UnitOfMeasureCode",
          gcType: "String",
          width: 100,
        },
        {
          gcLabel: "Standard Cost",
          dataField: "StandardCost",
          gcType: "Decimal-4-4",
        },
        {
          gcLabel: "Standard Sell",
          dataField: "StandardSell",
          gcType: "Decimal-4-4",
        },
        {
          gcLabel: "GP %",
          dataField: "GPPcnt",
          gcType: "Decimal-4-2",
          cellTemplate: "number-cell-template",
        },
        {
          dataField: "RRP",
          gcType: "Decimal-4-2",
          gcLabel: "RRP",
          // readOnly: true,
        },
        {
          dataField: "RRPConversionFactor",
          gcType: "Decimal-4-2",
          gcLabel: "RRP Conversion",
          // readOnly: true,
        },

        {
          gcLabel: "Conversion",
          dataField: "ConversionFactor",
          gcType: "Decimal-4-4",
        },

        {
          gcLabel: "Barcode",
          dataField: "Barcode",
          gcType: "String",
          width: 220,
        },

        // {
        //   gcLabel: "Variable",
        //   dataField: "IsVariable",
        //   gcType: "String",
        //   width: 100,
        //   cellTemplate: "variable-cell-template",
        // },
        {
          gcLabel: "Default Sales Unit",
          dataField: "IsDefault",
          gcType: "String",
          width: 100,
          cellTemplate: "checkbox-cell-template",
        },
        {
          gcLabel: "Default Production Unit",
          dataField: "IsPrimaryProductionUM",
          gcType: "String",
          width: 100,
          cellTemplate: "checkbox-cell-template",
          visible: this.IsProductionUnitVisible,
        },
        {
          gcLabel: "Production Unit",
          dataField: "IsProductionUM",
          gcType: "String",
          width: 100,
          cellTemplate: "checkbox-cell-template",
          visible: this.IsProductionUnitVisible,
        },
        {
          gcLabel: "Branch Code",
          dataField: "BranchCode",
          width: 120,
          gcType: "String",
          gcSubType: "HiddenCode",
        },
        {
          gcLabel: "Branch Description",
          dataField: "BranchDescription",
          width: 120,
          gcType: "String",
          gcSubType: "HiddenDescription",
        },
        {
          gcLabel: "Status",
          dataField: "Inactive",
          gcType: "String",
          gcSubType: "Chip",
          cellTemplate: "active-cell-template",
        },
      ]);
    },
    items() {
      let formFields = [
        {
          itemType: "group",
          caption: "",
          items: this.parseFormColumns([
            {
              dataField: "BranchCode",
              gcLabel: "Branch",
              visible: this.baseView.mode == "create",
              gcType: "String",
              editorType: "dxSelectBox",
              editorOptions: {
                dataSource: BranchService.dataSourceConfig("api/Branch", {
                  select: ["Description", "Code"],
                  filter: ["Inactive", "=", "N"],
                  sort: [{ selector: "Description", asc: true }],
                }),
                valueExpr: "Code",
                searchExpr: ["Description"],
                searchMode: "contains",
                showClearButton: true,
                itemTemplate: "branch",
                searchEnabled: true,
                displayExpr: "Description",
                onValueChanged: this.onBranchCodeChange,
              },
              validationRules: [
                {
                  type: "required",
                  message: "Branch is required",
                },
                {
                  type: "async",
                  validationCallback: this.branchCodeExistsRuleCallback,
                  message: "Branch does not exist",
                },
              ],
            },
            {
              dataField: "SalesUnitOfMeasureCode",
              gcLabel: "Sales UM",
              gcType: "String",
              editorType:
                this.baseView.mode != "review" ? "dxSelectBox" : "dxTextBox",
              editorOptions: {
                dataSource: UnitOfMeasureService.dataSourceConfig(
                  "api/UnitOfMeasure",
                  {
                    select: ["Description", "Code"],
                    filter: ["Inactive", "=", false],
                    sort: [{ selector: "Description", asc: true }],
                  }
                ),
                valueExpr: "Code",
                searchExpr: ["Description"],
                searchMode: "contains",
                showClearButton: true,
                itemTemplate: "unitOfMeasure",
                searchEnabled: true,
                displayExpr: "Description",
              },
              validationRules: [
                {
                  type: "required",
                  message: "Sales UM is required",
                },
                {
                  type: "async",
                  validationCallback: this.unitOfMeasureCodeExistsRuleCallback,
                  message: "Unit of Measure does not exist",
                },
              ],
            },
            {
              dataField: "ConversionFactor",
              gcType: "Decimal-4-4",
              gcLabel: "Conversion",
              visible:
                this.model.SalesUnitOfMeasureCode.toLowerCase() !=
                this.model.ProductUnitOfMeasureCode.toLowerCase(),
              editorOptions: {
                onChange: this.onConversionFactorChange,
              },
              validationRules: [
                {
                  type: "required",
                  message: "Conversion Rate is required",
                },
                {
                  type: "range",
                  message:
                    "Conversion must be between 0.0001 and 999,999,999.9999",
                  min: 0.0001,
                  max: 999999999.9999,
                },
              ],
            },
            {
              dataField: "StandardCostSalesUM",
              gcType: "Decimal-4-4",
              gcLabel: "Std Cost Sales UM",
              // gcDynamicLabel:
              //   "Std Cost (" + this.model.SalesUnitOfMeasureCode + ")",
              readOnly: true,
              editorOptions: {
                tabIndex: -1,
                onValueChanged: this.onCostChanged,
              },
            },

            {
              dataField: "StandardCostStockUM",
              gcType: "Decimal-4-4",
              gcLabel: "Std Cost Stock UM",
              // gcDynamicLabel:
              //   "Std Cost (" + this.model.ProductUnitOfMeasureCode + ")",
              readOnly: true,
              visible:
                this.model.SalesUnitOfMeasureCode.toLowerCase() !=
                this.model.ProductUnitOfMeasureCode.toLowerCase(),
              editorOptions: {
                tabIndex: -1,
              },
            },

            {
              dataField: "StandardSellSalesUM",
              gcType: "Decimal-4-4",
              // gcDynamicLabel:
              //   "Std Sell (" + this.model.SalesUnitOfMeasureCode + ")",
              gcLabel: "Std Sell Sales UM",
              validationRules: [
                {
                  type: "required",
                  message:
                    "Std Sell (" +
                    this.model.SalesUnitOfMeasureCode +
                    ") is required",
                },
                {
                  type: "range",
                  message:
                    "Std Sell must be between 0.0001 and 999,999,999.9999",
                  min: 0.0001,
                  max: 999999999.9999,
                },
              ],
              editorOptions: {
                onChange: this.onStdSellSalesUMChange,
              },
            },

            {
              dataField: "StandardSellStockUM",
              gcType: "Decimal-4-4",
              // gcDynamicLabel:
              //   "Std Sell (" + this.model.ProductUnitOfMeasureCode + ")",
              gclabel: "Std Sell Stock UM",
              visible: false,
              editorOptions: {
                tabIndex: -1,
              },
            },

            {
              dataField: "GPPercent",
              gcType: "Decimal-4-4",
              gcDynamicLabel:
                "GP %  (" + this.model.SalesUnitOfMeasureCode + ")",
              readOnly: true,
              editorOptions: {
                tabIndex: -1,
              },
            },
            {
              dataField: "RRP",
              gcType: "Decimal-4-2",
              gcDynamicLabel: "RRP",
              // readOnly: true,
            },
            {
              dataField: "RRPConversionFactor",
              gcType: "Decimal-4-2",
              gcDynamicLabel: "RRP Conversion",
              // readOnly: true,
            },
            {
              dataField: "Barcode",
              gcLabel: "Barcode",
              gcType: "String",
              editorType: "dxTextBox",
              editorOptions: {
                maxLength: 20,
              },
            },

            // Pieces
            // {
            //   itemType: "group",
            //   caption: "",
            //   items: [
            //     {
            //       dataField: "AllBranches",
            //       label: { text: "All Branches" },
            //       editorType: "dxSwitch",
            //     },
            //   ],
            // },
            {
              itemType: "group",
              caption: "",
              colCount: 2,
              gcType: "group",
              items: this.parseFormColumns([
                {
                  dataField: "IsDefault",
                  disabled: this.model.IsDefaultReadOnly,
                  gcLabel: "Default Sales Unit",
                  gcType: "Boolean",
                },
                {
                  dataField: "IsProductionUM",
                  gcLabel: "Production Unit",
                  gcType: "Boolean",
                  visible: this.IsProductionUnitVisible,
                  editorOptions: {
                    onValueChanged: this.onIsProductionUMChange,
                  },
                },
                {
                  dataField: "IsVariable",
                  gcLabel: "Variable Unit",
                  gcType: "Boolean",
                },
                {
                  dataField: "IsPrimaryProductionUM",
                  gcLabel: "Default Production Unit",
                  gcType: "Boolean",
                  visible: this.IsProductionUnitVisible,
                  editorOptions: {
                    onValueChanged: this.onIsPrimaryProductionUMChange,
                  },
                },
                {
                  dataField: "Inactive",
                  gcType: "Boolean",
                  editorOptions: {
                    readOnly: this.baseView.mode === "review",
                  },
                },
              ]),
            },
          ]),
        },
      ];

      return formFields;
    },
    dataSourceConfig() {
      return ProductUnitOfMeasureService.dataSourceConfigList(
        "api/ProductUnitOfMeasureList",
        {
          // select: [
          //   "ProductCode,BranchCode,BranchDescription,UnitOfMeasureCode,IsDefault,StandardSell,RecordId,StandardCost,GPPcnt,Barcode,ConversionFactor",
          // ],
          // sort: [{ selector: "ProductCode", desc: false }],
          filter: [["ProductCode", "=", this.model.ProductCode]],
        }
      );
    },

    tabs() {
      if (this.loadingCompleted)
        return ProductTabs(
          this.model.ProductCode ? this.model.ProductCode : this.baseView.id
        );
      else return [];
    },
    showLeftContainer() {
      return !(this.isXSmall && this.baseView.mode !== "");
    },
    showRightContainer() {
      if (
        (this.baseView.mode == "review" || this.baseView.mode == "edit") &&
        this.baseView.id.length < 36
      ) {
        return false;
      }

      return !(this.baseView.mode == "review" && this.baseView.id == "");
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler: "handleLocalRouteChange",
    },
    showRightContainer() {
      setTimeout(() => {
        let grid = this.$refs["productUnitOfMeasureGrid"].instance;
        grid.updateDimensions();
      }, 10);
    },
  },
  methods: {
    formatMyNumber(value, format) {
      return `(${formatNumber(value, format)})`;
    },
    onIsProductionUMChange(e) {
      if (!e.event) return;

      if (e.value == false) {
        this.model.IsPrimaryProductionUM = false;
      }
    },
    onIsPrimaryProductionUMChange(e) {
      if (!e.event) return;

      if (e.value) {
        this.model.IsProductionUM = true;
      }
    },
    onCostChanged(e) {
      this.model.GPPercent =
        ((this.model.StandardSellSalesUM - e.value) * 100) /
        (this.model.StandardSellSalesUM ? this.model.StandardSellSalesUM : 1);
    },

    async close() {
      if (this.baseView.mode == "create" || this.baseView.mode == "edit") {
        let confirmLeaving = await this.$confirm(
          "You have unsaved changes. Are you sure you want to leave?",
          "Unsaved changes"
        );

        if (confirmLeaving) {
          let grid = this.$refs["productUnitOfMeasureGrid"].instance;
          grid.clearSelection();

          this.baseView.id = this.model.ProductCode;
          this.setIgnoreRouteChangeConfirmation();
          this.$router.push({
            path: `/product-unit-of-measure/review/${this.baseView.id}`,
          });
        } else {
          this.next(false);
        }
      } else {
        let grid = this.$refs["productUnitOfMeasureGrid"].instance;
        grid.clearSelection();

        this.baseView.id = this.model.ProductCode;
        this.setIgnoreRouteChangeConfirmation();
        this.$router.push({
          path: `/product-unit-of-measure/review/${this.baseView.id}`,
        });
      }
    },
    backButtonFunction() {
      this.$logger.debug(`Form dirty:  ${this.dirty}`);
      if (this.isDirty == false) {
        this.setIgnoreRouteChangeConfirmation();
      }
      this.$router.push({
        path: "/product",
      });
    },
    onToolbarPreparing(e) {
      var toolbarItems = e.toolbarOptions.items;
      var searchPanel = toolbarItems.find((item) => {
        return item.name === "searchPanel";
      });
      var exportButton = toolbarItems.find((item) => {
        return item.name === "exportButton";
      });
      var columnChooserButton = toolbarItems.find((item) => {
        return item.name === "columnChooserButton";
      });

      e.toolbarOptions.items = toolbarItems.filter((item) => {
        return (
          item.name !== "exportButton" &&
          item.name !== "searchPanel" &&
          item.name !== "columnChooserButton"
        );
      });

      searchPanel.location = "after";
      exportButton.location = "after";

      e.toolbarOptions.items.unshift(
        {
          location: "before",
          widget: "dxButton",
          options: {
            icon: "plus",
            text: "Add New",
            onClick: () => {
              this.$logger.trackEventButtonClickAddNew();
              this.model.BranchCode = "";
              this.model.BranchDescription = "";
              this.model.SalesUnitOfMeasureCode = ""; // this is set when Sales UM is chosen
              this.model.ProductUnitOfMeasureCode = ""; // this is set at this point
              this.model.ConversionFactor = 1.0;
              (this.model.StandardCostSalesUM = 0.0),
                (this.model.StandardCostStockUM = 0.0), // this is set on selection of Branch
                (this.model.StandardSellSalesUM = 0.0001),
                (this.model.StandardSellStockUM = 0.0001),
                (this.model.GPPercent = 0),
                (this.model.Barcode = ""),
                (this.model.Pieces = 1),
                (this.model.IsDefault = true);
              (this.model.IsDefaultReadOnly = false),
                (this.model.AllBranches = true);
              this.model.RecordId = "00000000-0000-0000-0000-000000000000";
              this.model.DateChanged = "";
              this.model.DateCreated = "";
              this.model.ChangedBy = "";
              this.model.CreatedBy = "";
              this.model.Inactive = false;
              this.baseView.id = this.model.ProductCode;
              this.model.RRP = 0;
              this.model.RRPConversionFactor = 0;

              this.$router.push({
                path: `/product-unit-of-measure/create/${this.baseView.id}`,
              });
            },
          },
        },
        searchPanel,
        exportButton,
        columnChooserButton,
        {
          location: "after",
          locateinmenu: "auto",
          widget: "dxButton",
          options: {
            validationGroup: "formsData",
            icon: "filter",
            elementAttr: { id: "filter-btn" },
            onClick: () => {
              this.showFilterRightContainer = !this.showFilterRightContainer;
              var item = document.getElementById("filter-btn");
              this.$logger.debug("item ", item);
              item.classList.add("dx-state-invisible");
            },
          },
        }
        // exportButton
      );
    },
    async handleLocalRouteChange() {
      let that = this;
      if (
        (this.baseView.mode === "review" || this.baseView.mode === "edit") &&
        this.baseView.id.length == 36
      ) {
        if (this.baseView.id) this.showLoading(true);
        this.model.RecordId = this.baseView.id;
        this.getRecord().then((response) => {
          if (response.data.value.length > 0) {
            that.model.ProductCode = response.data.value[0].ProductCode;
            that.model.ProductDescription1 =
              response.data.value[0].Product.Description1;
            that.model.BranchCode = response.data.value[0].BranchCode;
            that.model.BranchDescription =
              response.data.value[0].Branch.Description;
            that.model.SalesUnitOfMeasureCode =
              response.data.value[0].UnitOfMeasureCode;
            that.model.ProductUnitOfMeasureCode =
              response.data.value[0].Product.UnitOfMeasureCode;
            (that.model.ConversionFactor =
              response.data.value[0].ConversionFactor),
              (that.model.StandardCostSalesUM =
                response.data.value[0].StandardCost),
              (that.model.StandardCostStockUM =
                response.data.value[0].StandardCost /
                (response.data.value[0].ConversionFactor == 0.0
                  ? 1
                  : response.data.value[0].ConversionFactor)),
              (that.model.StandardSellSalesUM =
                response.data.value[0].StandardSell),
              (that.model.StandardSellStockUM =
                response.data.value[0].UnitStandardSell),
              (that.model.Barcode = response.data.value[0].Barcode),
              (that.model.GPPercent =
                ((response.data.value[0].StandardSell -
                  response.data.value[0].StandardCost) /
                  (response.data.value[0].StandardSell == 0.0
                    ? 1
                    : response.data.value[0].StandardSell)) *
                100);

            // that.model.Pieces = response.data.value[0].Pieces,
            that.model.IsDefault =
              response.data.value[0].IsDefault == "Y" ? true : false;
            that.model.Inactive =
              response.data.value[0].Inactive == "Y" ? true : false;
            that.model.RecordId = response.data.value[0].RecordId;
            that.model.IsDefaultReadOnly = that.model.IsDefault;
            that.model.DateChanged = response.data.value[0].DateChanged;
            that.model.DateCreated = response.data.value[0].DateCreated;
            that.model.ChangedBy = response.data.value[0].ChangedBy;
            that.model.CreatedBy = response.data.value[0].CreatedBy;
            that.model.RRP = response.data.value[0].RRP;
            that.model.RRPConversionFactor =
              response.data.value[0].RRPConversionFactor;

            that.model.IsVariable =
              response.data.value[0].IsVariable == "Y" ? true : false;
            that.model.IsProductionUM =
              response.data.value[0].IsProductionUM == "Y" ? true : false;
            that.model.IsPrimaryProductionUM =
              response.data.value[0].IsPrimaryProductionUM == "Y"
                ? true
                : false;

            that.setProductionUnitVisible(response.data.value[0].Product);

            ProductTabsVisibility({
              text: "Kit Components",
              value: response.data.value[0].Product.IsKit == "Y" ? true : false,
            });

            // that.refreshView();
            // This fixes up case sensitity issues with remote data in SelectBox
            if (that.$refs["product-unit-of-measure"])
              that.validateFormConfig(
                that.$refs["product-unit-of-measure"].instance
              );
          }
          that.loadingCompleted = true;
          that.showLoading(false);
        });
      } else {
        if (!this.model.ProductCode) {
          await ProductService.getByCode(that.baseView.id).then((response2) => {
            if (response2.data.value.length > 0) {
              that.model.ProductCode = response2.data.value[0].Code;
              that.model.ProductDescription1 =
                response2.data.value[0].Description1;
              that.model.ProductUnitOfMeasureCode =
                response2.data.value[0].UnitOfMeasureCode;

              that.setProductionUnitVisible(response2.data.value[0]);

              ProductTabsVisibility({
                text: "Kit Components",
                value: response2.data.value[0].IsKit == "Y" ? true : false,
              });
            }
            this.loadingCompleted = true;
          });
        } else {
          if (this.showRightContainer) {
            await ProductService.getByCode(that.baseView.id).then(
              (response2) => {
                if (response2.data.value.length > 0) {
                  that.model.ProductUnitOfMeasureCode =
                    response2.data.value[0].UnitOfMeasureCode;
                  ProductTabsVisibility({
                    text: "Kit Components",
                    value: response2.data.value[0].IsKit == "Y" ? true : false,
                  });

                  that.setProductionUnitVisible(response2.data.value[0]);
                }
                this.loadingCompleted = true;
              }
            );
          } else this.loadingCompleted = true;
        }
        that.showLoading(false);
      }
    },

    async getDefaultProductionUnitOfMeasure() {
      try {
        const response =
          await ProductUnitOfMeasureService.getDefaultProductionUnitOfMeasure(
            this.model.ProductCode
          );

        if (
          response &&
          response.data &&
          response.data.value &&
          response.data.value.length === 0
        ) {
          this.model.IsPrimaryProductionUM = true;
        } else {
          this.model.IsPrimaryProductionUM = false;
        }
      } catch (error) {
        this.displayServerError(
          error,
          "Failed to fetch default production unit of measure"
        );
      }
    },

    setProductionUnitVisible(response) {
      if (response.Source === "M") {
        this.IsProductionUnitVisible = true;
      } else {
        this.IsProductionUnitVisible = false;
      }

      if (this.baseView.mode === "create" && this.IsProductionUnitVisible) {
        this.model.IsProductionUM = true;
        this.getDefaultProductionUnitOfMeasure();
      }
    },

    onBranchCodeChange() {
      ProductCostService.getByCodeAndBranch(
        this.model.ProductCode,
        this.model.BranchCode
      ).then((response3) => {
        if (response3.data.value.length > 0) {
          this.model.StandardCostStockUM = response3.data.value[0].StandardCost;
          this.model.StandardSellStockUM = response3.data.value[0].StandardSell;
          this.refreshView();
        }
      });
    },

    onConversionFactorChange() {
      // Std Sell Sales UM & Conversion is nominated by the User
      // System updates Std Sell Stock & Std Cost Stock when conversion factor or sales um is update

      this.model.StandardCostSalesUM =
        this.model.StandardCostStockUM * this.model.ConversionFactor;

      this.model.StandardSellSalesUM =
        this.model.StandardSellStockUM * this.model.ConversionFactor;

      // Sales - Cost / Sales = GP Margin
      this.model.GPPercent =
        ((this.model.StandardSellSalesUM - this.model.StandardCostSalesUM) /
          (this.model.StandardSellSalesUM == 0
            ? 1
            : this.model.StandardSellSalesUM)) *
        100;
    },

    onStdSellSalesUMChange() {
      // if (this.model.ConversionFactor == 0.0) {
      //   this.model.ConversionFactor == 1.0;
      // }
      // if (this.model.StandardSellSalesUM == 0.0) {
      //   this.model.StandardSellSalesUM == 1.0;
      // }

      this.model.StandardSellStockUM =
        this.model.StandardSellSalesUM /
        (this.model.ConversionFactor == 0 ? 1 : this.model.ConversionFactor);

      // Sales - Cost / Sales = GP Margin
      this.model.GPPercent =
        ((this.model.StandardSellSalesUM - this.model.StandardCostSalesUM) /
          (this.model.StandardSellSalesUM == 0
            ? 1
            : this.model.StandardSellSalesUM)) *
        100;
    },

    unitOfMeasureCodeExistsRuleCallback(params) {
      return new Promise((resolve) => {
        UnitOfMeasureService.getByCode(params.value).then((response) => {
          if (response.data.value.length > 0) {
            this.model.SalesUnitOfMeasureCode = response.data.value[0].Code;
            resolve(true);
          } else {
            resolve(false);
          }
        });
      });
    },

    branchCodeExistsRuleCallback(params) {
      return new Promise((resolve) => {
        BranchService.getByCode(params.value).then((response) => {
          if (response.data.value.length > 0) {
            this.model.BranchCode = response.data.value[0].Code;
            resolve(true);
          } else {
            resolve(false);
          }
        });
      });
    },

    async getRecord() {
      return ProductUnitOfMeasureService.getByRecordId(this.baseView.id);
    },
    onSelectionChanged({ selectedRowsData }) {
      if (selectedRowsData.length == 0) {
        return;
      }

      this.baseView.id = selectedRowsData[0].RecordId;
      this.$router.push({
        path: `/product-unit-of-measure/review/${selectedRowsData[0].RecordId}`,
      });
    },
    async deleteProductUnitOfMeasure() {
      let that = this;

      let confirmLeaving = await this.$confirm(
        `Are you sure you want to delete this Sales UM?`,
        "Delete Sales UM"
      );

      if (confirmLeaving) {
        this.showLoading(true);
        await ProductUnitOfMeasureService.delete({
          RecordId: this.model.RecordId,
        })
          // eslint-disable-next-line no-unused-vars
          .then((res) => {
            that.showLoading(false);
            that.close();
            let grid = this.$refs["productUnitOfMeasureGrid"].instance;
            grid.refresh();
          })
          .catch((error) => {
            this.displayServerError(error, "Sales UM delete failed");
          });
      }
    },
    async saveProductUnitOfMeasure(e) {
      const result = await e.validationGroup.validate();
      // eslint-disable-next-line no-console
      // this.$logger.debug("Model", this.model);
      if (!result.isValid) {
        this.displayValidationSummary();
        return;
      }
      this.showLoading(true);
      await ProductUnitOfMeasureService.save(this.baseView.mode, {
        AllBranches: this.model.AllBranches ? "Y" : "N",
        Barcode: this.model.Barcode,
        BranchCode: this.model.BranchCode,
        // CompressedCode: 000L,
        // IsVariable: N,
        ConversionFactor: this.model.ConversionFactor,
        IsDefault: this.model.IsDefault ? "Y" : "N",
        Inactive: this.model.Inactive ? "Y" : "N",
        ProductCode: this.model.ProductCode,
        RecordId: this.model.RecordId,
        StandardCost: this.model.StandardCostSalesUM,
        StandardSell: this.model.StandardSellSalesUM,
        UnitStandardSell: this.model.StandardSellStockUM,
        UnitOfMeasureCode: this.model.SalesUnitOfMeasureCode,
        RRP: this.model.RRP,
        RRPConversionFactor: this.model.RRPConversionFactor,
        IsProductionUM: this.model.IsProductionUM ? "Y" : "N",
        IsVariable: this.model.IsVariable ? "Y" : "N",
        IsPrimaryProductionUM: this.model.IsPrimaryProductionUM ? "Y" : "N",
      })
        .then((res) => {
          if (this.baseView.mode == "create") {
            this.model.RecordId = res.data.RecordId;
          }

          var msg = this.model.AllBranches
            ? "all branches"
            : `branch ${this.model.BranchDescription}`;

          this.showLoading(false);
          // eslint-disable-next-line no-console
          // this.$logger.debug("saveProductLocation", res);
          this.$notify(
            `Sales UM ${this.model.SalesUnitOfMeasureCode} for ${msg} saved successfully`,
            "success"
          );
          this.setIgnoreRouteChangeConfirmation();
          this.$router.push({
            path: `/product-unit-of-measure/review/${this.model.RecordId}`,
          });
        })
        .catch((error) => {
          this.displayServerError(error, "Sales UM save failed");
        });
    },
  },
};
</script>
